/**
 * LibraryGlobalRules — platform-level editor for the Universal +
 * Framework rule banks.
 *
 * Three tabs corresponding to the compiler's three rule sources:
 *   • Universal           — applies to every agent (inbound + outbound)
 *   • Framework — Inbound — applies to every inbound task agent
 *   • Framework — Outbound — applies to every outbound task agent
 *
 * Each tab loads its bank via /api/library/global-rules/:scope. If
 * there's no DB row yet, the API returns the in-code defaults so the
 * admin always starts from the current shipped rules. Saving creates
 * the first version (v1) in MongoDB.
 *
 * "Reset to in-code defaults" re-seeds the row from the JS files in
 * configural-runtime/src/agent-definitions/{universal-rules,framework-rules}.js.
 */

import React, { useCallback, useEffect, useState } from "react";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";
import * as api from "../api.js";
import GlobalRulesEditor from "../components/global-rules-editor.jsx";

const SCOPES = [
  { id: "universal",          label: "Universal",          accent: "#1565c0" },
  { id: "framework-inbound",  label: "Framework — Inbound", accent: "#7b1fa2" },
  { id: "framework-outbound", label: "Framework — Outbound", accent: "#a5824a" },
];

export default function LibraryGlobalRules() {
  const [activeScope, setActiveScope] = useState("universal");
  const [data, setData] = useState(null);   // { source, scope, rules, version }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPlatformGlobalRules(activeScope);
      setData(res.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [activeScope]);

  useEffect(() => { load(); }, [load]);

  const onSave = async (rules, commitMsg) => {
    await api.writePlatformGlobalRules(activeScope, rules, { commit_msg: commitMsg });
    await load();
  };

  const onResetToDefault = async () => {
    await api.resetPlatformGlobalRulesToDefaults(activeScope);
    await load();
  };

  return (
    <>
      <PageHeader
        title="Global Rules"
        subtitle="Universal + Framework rule banks merged into every agent prompt at runtime. Edits apply across all tenants. Sub-tenants can override per-scope from their Global Rules tab."
        breadcrumbs={[{ label: "Library", to: "/library" }, { label: "Global Rules" }]}
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        {/* Scope tab strip */}
        <div className="card" style={{ padding: 0, marginBottom: 16 }}>
          <div style={{ display: "flex", borderBottom: "1px solid #e5e5e5" }}>
            {SCOPES.map((s) => {
              const active = s.id === activeScope;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveScope(s.id)}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    background: active ? "#fff" : "#f8f9fa",
                    border: "none",
                    borderBottom: active ? `3px solid ${s.accent}` : "3px solid transparent",
                    color: active ? s.accent : "#666",
                    fontWeight: active ? 700 : 500,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <Loading />
        ) : data ? (
          <GlobalRulesEditor
            scope={activeScope}
            initialRules={data.rules}
            source={data.source}
            version={data.version}
            onSave={onSave}
            onResetToDefault={onResetToDefault}
            resetButtonLabel="Reset to in-code defaults"
            context="library"
          />
        ) : null}
      </div>
    </>
  );
}
